import React, { useRef, useState, useEffect } from 'react';
import Api from "../Api/Api.json";
import { Snackbar, Alert } from "@mui/material";

const SimulatedEditor = ({ presignedUrl, date }) => {
  const videoRef = useRef(null);
  const [start, setStart] = useState(10);
  const [end, setEnd] = useState(20);
  const [textOverlay, setTextOverlay] = useState('Hello World');
  const [imageOverlayUrl, setImageOverlayUrl] = useState('');
  const [audioReplacement, setAudioReplacement] = useState('');
  const [filters, setFilters] = useState('none');
  const [showGrid, setShowGrid] = useState(false);
  const [timelineMarkers, setTimelineMarkers] = useState([]);
  const [newMarkerTime, setNewMarkerTime] = useState('');
  const [tracks, setTracks] = useState([{ id: 1, name: 'Video Track 1' }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cuts, setCuts] = useState([]);
  const [newCutStart, setNewCutStart] = useState('');
  const [newCutEnd, setNewCutEnd] = useState('');
  const [textOverlays, setTextOverlays] = useState([]);
  const [newTextOverlay, setNewTextOverlay] = useState({
    text: '',
    startTime: '',
    endTime: '',
    xPosition: 50,
    yPosition: 50,
    color: '#ffffff',
    size: 16,
    font: 'Arial',
    backgroundColor: 'rgba(0, 0, 0, 0.5)'
  });
  const [imageOverlays, setImageOverlays] = useState([]);
  const [newImageOverlay, setNewImageOverlay] = useState({
    file: null,
    startTime: '',
    endTime: '',
    xPosition: 50,
    yPosition: 50,
    width: 20,
    height: 20,
    opacity: 0.8
  });
  const [audioOverlays, setAudioOverlays] = useState([]);
  const [newAudioOverlay, setNewAudioOverlay] = useState({
    file: null,
    startTime: 0,
    endTime: 0,
    audioType: 'background', // 'background' or 'only'
    volume: 0.8
  });
  const [voiceovers, setVoiceovers] = useState([]);
  const [keepCurrentAudio, setKeepCurrentAudio] = useState('yes');
  const [newVoiceover, setNewVoiceover] = useState({
    startTime: '0:00',
    text: '',
    speakerGender: 'female',
    emotion: '',
    modulation: 5
  });
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});
  
  const base_url = Api.MAGICVERSE_USER_API;

  useEffect(() => {
    const video = videoRef.current;
    const handleTimeUpdate = () => {
      // Track current time for better reactivity
      setCurrentVideoTime(video.currentTime);
    };
    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [start, end]);

  useEffect(() => {
    const video = videoRef.current;
    const handleLoadedMetadata = () => {
      setVideoDuration(video.duration);
      setNewAudioOverlay(prev => ({ ...prev, endTime: video.duration }));
    };
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => video.removeEventListener('loadedmetadata', handleLoadedMetadata);
  }, []);

  const addTimelineMarker = () => {
    const time = parseFloat(newMarkerTime);
    if (!isNaN(time)) {
      setTimelineMarkers([...timelineMarkers, time]);
      setNewMarkerTime('');
    }
  };

  const addCut = () => {
    const startTime = parseFloat(newCutStart);
    const endTime = parseFloat(newCutEnd);
    if (!isNaN(startTime) && !isNaN(endTime) && startTime < endTime) {
      setCuts([...cuts, { id: Date.now(), start: startTime, end: endTime }]);
      setNewCutStart('');
      setNewCutEnd('');
    }
  };

  const removeCut = (cutId) => {
    setCuts(cuts.filter(cut => cut.id !== cutId));
  };

  const addTextOverlay = () => {
    const startTime = parseFloat(newTextOverlay.startTime);
    const endTime = parseFloat(newTextOverlay.endTime);
    if (newTextOverlay.text.trim() && !isNaN(startTime) && !isNaN(endTime) && startTime < endTime) {
      setTextOverlays([...textOverlays, { ...newTextOverlay, id: Date.now() }]);
      setNewTextOverlay({
        text: '',
        startTime: '',
        endTime: '',
        xPosition: 50,
        yPosition: 50,
        color: '#ffffff',
        size: 16,
        font: 'Arial',
        backgroundColor: 'rgba(0, 0, 0, 0.5)'
      });
    }
  };

  const removeTextOverlay = (overlayId) => {
    setTextOverlays(textOverlays.filter(overlay => overlay.id !== overlayId));
  };

  const updateTextOverlay = (overlayId, field, value) => {
    setTextOverlays(textOverlays.map(overlay => 
      overlay.id === overlayId ? { ...overlay, [field]: value } : overlay
    ));
  };

  const addImageOverlay = () => {
    const startTime = parseFloat(newImageOverlay.startTime);
    const endTime = parseFloat(newImageOverlay.endTime);
    console.log('Adding image overlay:', { newImageOverlay, startTime, endTime });
    if (newImageOverlay.file && !isNaN(startTime) && !isNaN(endTime) && startTime < endTime) {
      // Convert file to base64 for better persistence
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target.result; // This is the base64 data URL
        const newOverlay = { ...newImageOverlay, id: Date.now(), imageUrl };
        console.log('Created new overlay:', newOverlay);
        setImageOverlays([...imageOverlays, newOverlay]);
        setNewImageOverlay({
          file: null,
          startTime: '',
          endTime: '',
          xPosition: 50,
          yPosition: 50,
          width: 20,
          height: 20,
          opacity: 0.8
        });
      };
      reader.onerror = (error) => {
        console.error('Error reading file:', error);
      };
      reader.readAsDataURL(newImageOverlay.file);
    } else {
      console.log('Validation failed:', { 
        hasFile: !!newImageOverlay.file, 
        startTime, 
        endTime, 
        isValid: !isNaN(startTime) && !isNaN(endTime) && startTime < endTime 
      });
    }
  };

  const removeImageOverlay = (overlayId) => {
    setImageOverlays(imageOverlays.filter(overlay => overlay.id !== overlayId));
  };

  const updateImageOverlay = (overlayId, field, value) => {
    setImageOverlays(imageOverlays.map(overlay => 
      overlay.id === overlayId ? { ...overlay, [field]: value } : overlay
    ));
  };

  const resetNewImageOverlay = () => {
    setNewImageOverlay({
      file: null,
      startTime: '',
      endTime: '',
      xPosition: 50,
      yPosition: 50,
      width: 20,
      height: 20,
      opacity: 0.8
    });
  };

  const addAudioOverlay = () => {
    const startTime = parseFloat(newAudioOverlay.startTime);
    const endTime = parseFloat(newAudioOverlay.endTime);
    if (newAudioOverlay.file && !isNaN(startTime) && !isNaN(endTime) && startTime < endTime) {
      const audioUrl = URL.createObjectURL(newAudioOverlay.file);
      setAudioOverlays([...audioOverlays, { ...newAudioOverlay, id: Date.now(), audioUrl }]);
      setNewAudioOverlay({
        file: null,
        startTime: 0,
        endTime: videoDuration,
        audioType: 'background',
        volume: 0.8
      });
    }
  };

  const removeAudioOverlay = (overlayId) => {
    setAudioOverlays(audioOverlays.filter(overlay => overlay.id !== overlayId));
  };

  const updateAudioOverlay = (overlayId, field, value) => {
    setAudioOverlays(audioOverlays.map(overlay => 
      overlay.id === overlayId ? { ...overlay, [field]: value } : overlay
    ));
  };

  const addVoiceover = () => {
    if (newVoiceover.text.trim()) {
      setVoiceovers([...voiceovers, { ...newVoiceover, id: Date.now() }]);
      setNewVoiceover({
        startTime: '0:00',
        text: '',
        speakerGender: 'female',
        emotion: '',
        modulation: 5
      });
    }
  };

  const removeVoiceover = (voiceoverId) => {
    setVoiceovers(voiceovers.filter(voiceover => voiceover.id !== voiceoverId));
  };

  const updateVoiceover = (voiceoverId, field, value) => {
    setVoiceovers(voiceovers.map(voiceover => 
      voiceover.id === voiceoverId ? { ...voiceover, [field]: value } : voiceover
    ));
  };
  
	const handleCloseSnackbar = () => {
		setSnackbar({ ...snackbar, open: false });
	};

  // Function to generate base64 image from text overlay
  const generateTextOverlayImage = async (textOverlay) => {
    return new Promise((resolve) => {
      // Create a canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set font properties at original size first to measure
      ctx.font = `${textOverlay.size}px ${textOverlay.font}`;
      
      // Measure text to get dimensions at original size
      const textMetrics = ctx.measureText(textOverlay.text);
      const textWidth = textMetrics.width;
      const textHeight = textOverlay.size; // Approximate height
      
      // Add minimal padding around the text
      const padding = 5;
      const canvasWidth = textWidth + (padding * 2);
      const canvasHeight = textHeight + (padding * 2);
      
      // Increase resolution by 3x for better quality
      const scale = 3;
      const scaledWidth = canvasWidth * scale;
      const scaledHeight = canvasHeight * scale;
      
      // Set canvas size to scaled dimensions
      canvas.width = scaledWidth;
      canvas.height = scaledHeight;
      
      // Scale the context to maintain the same visual size but with higher resolution
      ctx.scale(scale, scale);
      
      // Clear canvas
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      
      // Draw background if specified
      if (textOverlay.backgroundColor && textOverlay.backgroundColor !== 'transparent') {
        ctx.fillStyle = textOverlay.backgroundColor;
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      }
      
      // Draw text centered in the canvas
      ctx.fillStyle = textOverlay.color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(textOverlay.text, canvasWidth / 2, canvasHeight / 2);
      
      // Convert to base64
      const base64 = canvas.toDataURL('image/png');
      
      // Console log the base64 string for debugging
      console.log('=== TEXT OVERLAY BASE64 ===');
      console.log('Text:', textOverlay.text);
      console.log('Font:', textOverlay.font);
      console.log('Size:', textOverlay.size);
      console.log('Color:', textOverlay.color);
      console.log('Background:', textOverlay.backgroundColor);
      console.log('Original Dimensions:', `${canvasWidth}x${canvasHeight}`);
      console.log('Scaled Dimensions:', `${scaledWidth}x${scaledHeight}`);
      console.log('Base64:', base64);
      console.log('=== END BASE64 ===');
      
      resolve(base64);
    });
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const auth_user = await localStorage.getItem("auth_user");
      const id_token = JSON.parse(auth_user)?.id_token;

      // Convert image to base64 if present
      let imageBase64 = null;
      /*
      if (imageOverlayUrl instanceof File) {
        const reader = new FileReader();
        imageBase64 = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(imageOverlayUrl);
        });
      }
      */

      // Handle audio file upload if present
      let audioUrl = null;
      if (audioReplacement instanceof File) {
        // Get presigned URL for audio upload
        const presignedResponse = await fetch(base_url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${id_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            task: "getEditVideoReplacementAudioPresignedURL",
            file_name: audioReplacement.name,
            content_type: audioReplacement.type,
            date: date
          })
        });

        if (!presignedResponse.ok) {
          throw new Error(`Failed to get presigned URL: ${presignedResponse.status}`);
        }

        const { presigned_url, file_url } = await presignedResponse.json();

        // Upload audio file to presigned URL
        const uploadResponse = await fetch(presigned_url, {
          method: "PUT",
          body: audioReplacement,
          headers: {
            "Content-Type": audioReplacement.type
          }
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload audio: ${uploadResponse.status}`);
        }

        audioUrl = file_url;
      }

      // Generate base64 images for text overlays
      const textOverlaysWithImages = await Promise.all(
        textOverlays.map(async (overlay) => {
          const textImage = await generateTextOverlayImage(overlay);
          console.log("textImage", textImage);
          // Calculate the actual image dimensions (matching the generateTextOverlayImage function)
          const scale = 3;
          const padding = 5;
          
          // Create a temporary canvas to measure text properly
          const tempCanvas = document.createElement('canvas');
          const tempCtx = tempCanvas.getContext('2d');
          tempCtx.font = `${overlay.size}px ${overlay.font}`;
          const textMetrics = tempCtx.measureText(overlay.text);
          
          const textWidth = textMetrics.width;
          const textHeight = overlay.size;
          const imageWidth = (textWidth + (padding * 2)) * scale;
          const imageHeight = (textHeight + (padding * 2)) * scale;
          
          return {
            ...overlay,
            textImage,
            imageWidth: Math.round(imageWidth),
            imageHeight: Math.round(imageHeight)
          };
        })
      );
      //return;
      // Create and submit the manifest
      const manifest = {
        render_type: "render_edit_video",
        created_at: date,
        start_time: start,
        end_time: end,
        text_overlay: textOverlay,
        text_overlays: textOverlaysWithImages,
        image_overlays: imageOverlays,
        voiceover: {
          entries: voiceovers.map(voiceover => ({ ...voiceover, language: 'english' })),
          keepCurrentAudio: keepCurrentAudio
        },
        audio_overlays: audioOverlays,
        audio_replacement: audioUrl,
        filter: filters,
        show_grid: showGrid,
        timeline_markers: timelineMarkers,
        tracks: tracks,
        cuts: cuts,
        overlay_image_present: imageOverlayUrl instanceof File
      };
      //console.log('Manifest:', manifest);
      //return;

      const response = await fetch(base_url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${id_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          task: "submitEditVideoRenderRequest",
          edit_config: manifest,
          image_overlay: "" //imageBase64
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`HTTP error! Status: ${response.status}, Message: ${JSON.stringify(errorData)}`);
      }

      const result = await response.json();
      console.log('Edit submitted successfully:', result);
      if (result.code === 200) {
				setSnackbar({
					open: true,
					message:
						"Your script has been submitted. You'll receive an email (usually 5-10 minutes) when the video is ready.",
					severity: "success",
				});
			} else {
				setSnackbar({
					open: true,
					message: "Something went wrong. Please try again later.",
					severity: "error",
				});
			}
      // Handle successful submission (e.g., show success message, redirect, etc.)

    } catch (error) {
      console.error('Error submitting edit:', error);
      // Handle error (e.g., show error message)
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'relative', width: '640px' }}>
      <div style={{ position: 'relative' }}>
        <video
          ref={videoRef}
          width="640"
          controls
          src={presignedUrl}
          style={{ filter: filters }}
        />

        {/* Debug Info */}
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '8px',
          borderRadius: '4px',
          fontSize: '12px',
          zIndex: 20
        }}>
          <div>Time: {currentVideoTime.toFixed(2)}s</div>
          <div>Duration: {videoDuration.toFixed(2)}s</div>
          <div>Overlays: {imageOverlays.filter(overlay => {
            return currentVideoTime >= overlay.startTime && currentVideoTime <= overlay.endTime;
          }).length}</div>
        </div>

        {/* Text Overlay */}
        {textOverlays.map((overlay) => {
          const currentTime = videoRef.current?.currentTime || 0;
          const isVisible = currentTime >= overlay.startTime && currentTime <= overlay.endTime;
          
          return isVisible ? (
            <div key={overlay.id} style={{
              position: 'absolute',
              left: `${overlay.xPosition}%`,
              top: `${overlay.yPosition}%`,
              transform: 'translate(-50%, -50%)',
              color: overlay.color,
              backgroundColor: overlay.backgroundColor,
              padding: '5px 10px',
              borderRadius: '5px',
              pointerEvents: 'none',
              fontFamily: overlay.font,
              fontSize: `${overlay.size}px`,
              whiteSpace: 'nowrap',
              zIndex: 10
            }}>
              {overlay.text}
            </div>
          ) : null;
        })}

        {/* Image Overlay */}
        {imageOverlays.map((overlay) => {
          const isVisible = currentVideoTime >= overlay.startTime && currentVideoTime <= overlay.endTime;
          
          // Debug logging for timing
          if (Math.abs(currentVideoTime - overlay.startTime) < 0.5 || Math.abs(currentVideoTime - overlay.endTime) < 0.5) {
            console.log(`Image overlay timing check:`, {
              overlayId: overlay.id,
              fileName: overlay.file.name,
              currentTime: currentVideoTime.toFixed(2),
              startTime: overlay.startTime,
              endTime: overlay.endTime,
              isVisible,
              timeRange: `${overlay.startTime}-${overlay.endTime}`
            });
          }
          
          return isVisible ? (
            <img
              key={overlay.id}
              src={overlay.imageUrl}
              alt="Overlay"
              style={{
                position: 'absolute',
                left: `${overlay.xPosition}%`,
                top: `${overlay.yPosition}%`,
                transform: 'translate(-50%, -50%)',
                width: `${overlay.width}%`,
                height: `${overlay.height}%`,
                opacity: overlay.opacity,
                pointerEvents: 'none',
                zIndex: 5
              }}
              onError={(e) => {
                console.error('Failed to load image overlay:', overlay.file.name, e);
                e.target.style.display = 'none';
              }}
              onLoad={() => {
                console.log('Successfully loaded image overlay:', overlay.file.name);
              }}
            />
          ) : null;
        })}

        {/* Grid Overlay */}
        {showGrid && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gridTemplateRows: 'repeat(3, 1fr)',
            pointerEvents: 'none'
          }}>
            {[...Array(9)].map((_, i) => (
              <div key={i} style={{ border: '1px dashed rgba(255, 255, 255, 0.3)' }}></div>
            ))}
          </div>
        )}
      </div>

      <div style={{ 
        marginTop: '20px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '16px',
        backgroundColor: '#f9f9f9',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h4 style={{ margin: '0 0 16px 0', color: '#333', fontSize: '18px' }}>Cuts</h4>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
          <input 
            type="number" 
            value={newCutStart} 
            onChange={(e) => setNewCutStart(e.target.value)} 
            placeholder="Start time (s)" 
            style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '120px' }}
          />
          <span style={{ color: '#666' }}>to</span>
          <input 
            type="number" 
            value={newCutEnd} 
            onChange={(e) => setNewCutEnd(e.target.value)} 
            placeholder="End time (s)" 
            style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '120px' }}
          />
          <button 
            onClick={addCut}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Add Cut
          </button>
        </div>
        {cuts.length > 0 ? (
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {cuts.map((cut) => (
              <div 
                key={cut.id} 
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  margin: '4px 0',
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px'
                }}
              >
                <span style={{ color: '#333' }}>
                  {cut.start}s - {cut.end}s
                </span>
                <button 
                  onClick={() => removeCut(cut.id)}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: '#666', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
            No cuts added yet
          </div>
        )}
      </div>

      <div style={{ 
        marginTop: '20px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '16px',
        backgroundColor: '#f9f9f9',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h4 style={{ margin: '0 0 16px 0', color: '#333', fontSize: '18px' }}>Text Overlays</h4>
        
        {/* Add New Text Overlay Form */}
        <div style={{ 
          border: '1px solid #e0e0e0', 
          borderRadius: '6px', 
          padding: '16px', 
          backgroundColor: 'white',
          marginBottom: '16px'
        }}>
          <h5 style={{ margin: '0 0 12px 0', color: '#555' }}>Add New Text Overlay</h5>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                Text:
              </label>
              <input 
                type="text" 
                value={newTextOverlay.text} 
                onChange={(e) => setNewTextOverlay({...newTextOverlay, text: e.target.value})}
                placeholder="Enter text"
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                Font:
              </label>
              <select 
                value={newTextOverlay.font} 
                onChange={(e) => setNewTextOverlay({...newTextOverlay, font: e.target.value})}
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              >
                <option value="Arial">Arial</option>
                <option value="Helvetica">Helvetica</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Georgia">Georgia</option>
                <option value="Verdana">Verdana</option>
                <option value="Courier New">Courier New</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                Start Time (s):
              </label>
              <input 
                type="number" 
                value={newTextOverlay.startTime} 
                onChange={(e) => setNewTextOverlay({...newTextOverlay, startTime: e.target.value})}
                placeholder="0"
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                End Time (s):
              </label>
              <input 
                type="number" 
                value={newTextOverlay.endTime} 
                onChange={(e) => setNewTextOverlay({...newTextOverlay, endTime: e.target.value})}
                placeholder="10"
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                Font Size:
              </label>
              <input 
                type="number" 
                value={newTextOverlay.size} 
                onChange={(e) => setNewTextOverlay({...newTextOverlay, size: parseInt(e.target.value)})}
                min="8"
                max="72"
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                Text Color:
              </label>
              <input 
                type="color" 
                value={newTextOverlay.color} 
                onChange={(e) => setNewTextOverlay({...newTextOverlay, color: e.target.value})}
                style={{ width: '100%', height: '40px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
              Background Color:
            </label>
            <input 
              type="color" 
              value={newTextOverlay.backgroundColor} 
              onChange={(e) => setNewTextOverlay({...newTextOverlay, backgroundColor: e.target.value})}
              style={{ width: '100%', height: '40px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                Horizontal Position (%):
              </label>
              <input 
                type="range" 
                min="0" 
                max="100" 
                value={newTextOverlay.xPosition} 
                onChange={(e) => setNewTextOverlay({...newTextOverlay, xPosition: parseInt(e.target.value)})}
                style={{ width: '100%' }}
              />
              <span style={{ fontSize: '12px', color: '#666' }}>{newTextOverlay.xPosition}%</span>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                Vertical Position (%):
              </label>
              <input 
                type="range" 
                min="0" 
                max="100" 
                value={newTextOverlay.yPosition} 
                onChange={(e) => setNewTextOverlay({...newTextOverlay, yPosition: parseInt(e.target.value)})}
                style={{ width: '100%' }}
              />
              <span style={{ fontSize: '12px', color: '#666' }}>{newTextOverlay.yPosition}%</span>
            </div>
          </div>

          <button 
            onClick={addTextOverlay}
            style={{
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Add Text Overlay
          </button>
        </div>

        {/* Existing Text Overlays List */}
        {textOverlays.length > 0 ? (
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <h5 style={{ margin: '0 0 12px 0', color: '#555' }}>Added Text Overlays</h5>
            {textOverlays.map((overlay) => (
              <div 
                key={overlay.id} 
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '12px',
                  margin: '8px 0',
                  backgroundColor: 'white'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 'bold', color: '#333' }}>"{overlay.text}"</span>
                  <button 
                    onClick={() => removeTextOverlay(overlay.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Remove
                  </button>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px', color: '#666' }}>
                  <div>Time: {overlay.startTime}s - {overlay.endTime}s</div>
                  <div>Position: {overlay.xPosition}%, {overlay.yPosition}%</div>
                  <div>Font: {overlay.font} {overlay.size}px</div>
                  <div>Color: <span style={{ color: overlay.color }}>■</span> {overlay.color}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: '#666', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
            No text overlays added yet
          </div>
        )}
      </div>

      <div style={{ 
        marginTop: '20px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '16px',
        backgroundColor: '#f9f9f9',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h4 style={{ margin: '0 0 16px 0', color: '#333', fontSize: '18px' }}>Image Overlays</h4>
        
        {/* Add New Image Overlay Form */}
        <div style={{ 
          border: '1px solid #e0e0e0', 
          borderRadius: '6px', 
          padding: '16px', 
          backgroundColor: 'white',
          marginBottom: '16px'
        }}>
          <h5 style={{ margin: '0 0 12px 0', color: '#555' }}>Add New Image Overlay</h5>
          
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
              Image File:
            </label>
            <input 
              type="file" 
              accept="image/*" 
              onChange={(e) => setNewImageOverlay({...newImageOverlay, file: e.target.files[0]})}
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              key={newImageOverlay.file ? 'has-file' : 'no-file'}
            />
            {newImageOverlay.file && (
              <div style={{ marginTop: '5px', fontSize: '0.9em', color: '#666' }}>
                Selected: {newImageOverlay.file.name}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                Start Time (s):
              </label>
              <input 
                type="number" 
                value={newImageOverlay.startTime} 
                onChange={(e) => setNewImageOverlay({...newImageOverlay, startTime: e.target.value})}
                placeholder="0"
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                End Time (s):
              </label>
              <input 
                type="number" 
                value={newImageOverlay.endTime} 
                onChange={(e) => setNewImageOverlay({...newImageOverlay, endTime: e.target.value})}
                placeholder="10"
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                Width (%):
              </label>
              <input 
                type="number" 
                value={newImageOverlay.width} 
                onChange={(e) => setNewImageOverlay({...newImageOverlay, width: parseInt(e.target.value)})}
                min="10"
                max="100"
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                Height (%):
              </label>
              <input 
                type="number" 
                value={newImageOverlay.height} 
                onChange={(e) => setNewImageOverlay({...newImageOverlay, height: parseInt(e.target.value)})}
                min="10"
                max="100"
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
              Opacity:
            </label>
            <input 
              type="range" 
              min="0" 
              max="1" 
              step="0.1"
              value={newImageOverlay.opacity} 
              onChange={(e) => setNewImageOverlay({...newImageOverlay, opacity: parseFloat(e.target.value)})}
              style={{ width: '100%' }}
            />
            <span style={{ fontSize: '12px', color: '#666' }}>{Math.round(newImageOverlay.opacity * 100)}%</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                Horizontal Position (%):
              </label>
              <input 
                type="range" 
                min="0" 
                max="100" 
                value={newImageOverlay.xPosition} 
                onChange={(e) => setNewImageOverlay({...newImageOverlay, xPosition: parseInt(e.target.value)})}
                style={{ width: '100%' }}
              />
              <span style={{ fontSize: '12px', color: '#666' }}>{newImageOverlay.xPosition}%</span>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                Vertical Position (%):
              </label>
              <input 
                type="range" 
                min="0" 
                max="100" 
                value={newImageOverlay.yPosition} 
                onChange={(e) => setNewImageOverlay({...newImageOverlay, yPosition: parseInt(e.target.value)})}
                style={{ width: '100%' }}
              />
              <span style={{ fontSize: '12px', color: '#666' }}>{newImageOverlay.yPosition}%</span>
            </div>
          </div>

          <button 
            onClick={addImageOverlay}
            style={{
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              marginRight: '10px'
            }}
          >
            Add Image Overlay
          </button>
          <button 
            onClick={resetNewImageOverlay}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Reset Form
          </button>
        </div>

        {/* Existing Image Overlays List */}
        {imageOverlays.length > 0 ? (
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <h5 style={{ margin: '0 0 12px 0', color: '#555' }}>Added Image Overlays</h5>
            {imageOverlays.map((overlay) => (
              <div 
                key={overlay.id} 
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '12px',
                  margin: '8px 0',
                  backgroundColor: 'white'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 'bold', color: '#333' }}>{overlay.file.name}</span>
                  <button 
                    onClick={() => removeImageOverlay(overlay.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Remove
                  </button>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px', color: '#666' }}>
                  <div>Time: {overlay.startTime}s - {overlay.endTime}s</div>
                  <div>Position: {overlay.xPosition}%, {overlay.yPosition}%</div>
                  <div>Size: {overlay.width}% × {overlay.height}%</div>
                  <div>Opacity: {Math.round(overlay.opacity * 100)}%</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: '#666', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
            No image overlays added yet
          </div>
        )}
      </div>

      <div style={{ 
        marginTop: '20px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '16px',
        backgroundColor: '#f9f9f9',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h4 style={{ margin: '0 0 16px 0', color: '#333', fontSize: '18px' }}>Voiceover</h4>
        
        {/* Global Voiceover Settings */}
        <div style={{ 
          border: '1px solid #e0e0e0', 
          borderRadius: '6px', 
          padding: '16px', 
          backgroundColor: 'white',
          marginBottom: '16px'
        }}>
          <h5 style={{ margin: '0 0 12px 0', color: '#555' }}>Global Settings</h5>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
              Keep Current Audio:
            </label>
            <select 
              value={keepCurrentAudio} 
              onChange={(e) => setKeepCurrentAudio(e.target.value)}
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>
        
        {/* Add New Voiceover Form */}
        <div style={{ 
          border: '1px solid #e0e0e0', 
          borderRadius: '6px', 
          padding: '16px', 
          backgroundColor: 'white',
          marginBottom: '16px'
        }}>
          <h5 style={{ margin: '0 0 12px 0', color: '#555' }}>Add New Voiceover</h5>
          
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
              Start Time:
            </label>
            <input 
              type="text" 
              value={newVoiceover.startTime} 
              onChange={(e) => setNewVoiceover({...newVoiceover, startTime: e.target.value})}
              placeholder="0:00"
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
              Text:
            </label>
            <textarea 
              value={newVoiceover.text} 
              onChange={(e) => setNewVoiceover({...newVoiceover, text: e.target.value})}
              placeholder="Enter voiceover text"
              rows="3"
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                Speaker Gender:
              </label>
              <select 
                value={newVoiceover.speakerGender} 
                onChange={(e) => setNewVoiceover({...newVoiceover, speakerGender: e.target.value})}
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              >
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="neutral">Neutral</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                Emotion:
              </label>
              <input 
                type="text" 
                value={newVoiceover.emotion} 
                onChange={(e) => setNewVoiceover({...newVoiceover, emotion: e.target.value})}
                placeholder="e.g., happy, sad, excited"
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
              Modulation: {newVoiceover.modulation}/10
            </label>
            <input 
              type="range" 
              min="1" 
              max="10" 
              value={newVoiceover.modulation} 
              onChange={(e) => setNewVoiceover({...newVoiceover, modulation: parseInt(e.target.value)})}
              style={{ width: '100%' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666', marginTop: '4px' }}>
              <span>Low</span>
              <span>High</span>
            </div>
          </div>

          <button 
            onClick={addVoiceover}
            style={{
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Add Voiceover
          </button>
        </div>

        {/* Existing Voiceovers List */}
        {voiceovers.length > 0 ? (
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <h5 style={{ margin: '0 0 12px 0', color: '#555' }}>Added Voiceovers</h5>
            {voiceovers.map((voiceover) => (
              <div 
                key={voiceover.id} 
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '12px',
                  margin: '8px 0',
                  backgroundColor: 'white'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 'bold', color: '#333' }}>Voiceover #{voiceover.id}</span>
                  <button 
                    onClick={() => removeVoiceover(voiceover.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Remove
                  </button>
                </div>
                
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontWeight: '500', color: '#333', marginBottom: '4px' }}>Text:</div>
                  <div style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>"{voiceover.text}"</div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px', color: '#666' }}>
                  <div>Start Time: {voiceover.startTime}</div>
                  <div>Gender: {voiceover.speakerGender}</div>
                  <div>Emotion: {voiceover.emotion || 'None'}</div>
                  <div>Modulation: {voiceover.modulation}/10</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: '#666', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
            No voiceovers added yet
          </div>
        )}
      </div>

      <div style={{ 
        marginTop: '20px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '16px',
        backgroundColor: '#f9f9f9',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h4 style={{ margin: '0 0 16px 0', color: '#333', fontSize: '18px' }}>Audio Overlays</h4>
        
        {/* Add New Audio Overlay Form */}
        <div style={{ 
          border: '1px solid #e0e0e0', 
          borderRadius: '6px', 
          padding: '16px', 
          backgroundColor: 'white',
          marginBottom: '16px'
        }}>
          <h5 style={{ margin: '0 0 12px 0', color: '#555' }}>Add New Audio Overlay</h5>
          
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
              Audio File:
            </label>
            <input 
              type="file" 
              accept="audio/*" 
              onChange={(e) => setNewAudioOverlay({...newAudioOverlay, file: e.target.files[0]})}
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
            {newAudioOverlay.file && (
              <div style={{ marginTop: '5px', fontSize: '0.9em', color: '#666' }}>
                Selected: {newAudioOverlay.file.name}
              </div>
            )}
            {newAudioOverlay.file && (
              <audio controls src={URL.createObjectURL(newAudioOverlay.file)} style={{ display: 'block', marginTop: '10px', width: '100%' }} />
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                Start Time (s):
              </label>
              <input 
                type="number" 
                value={newAudioOverlay.startTime} 
                onChange={(e) => setNewAudioOverlay({...newAudioOverlay, startTime: parseFloat(e.target.value) || 0})}
                min="0"
                step="0.1"
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                End Time (s):
              </label>
              <input 
                type="number" 
                value={newAudioOverlay.endTime} 
                onChange={(e) => setNewAudioOverlay({...newAudioOverlay, endTime: parseFloat(e.target.value) || videoDuration})}
                min="0"
                step="0.1"
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                Audio Type:
              </label>
              <select 
                value={newAudioOverlay.audioType} 
                onChange={(e) => setNewAudioOverlay({...newAudioOverlay, audioType: e.target.value})}
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              >
                <option value="background">Background Audio</option>
                <option value="only">Only Audio (Replace Original)</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>
                Volume:
              </label>
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.1"
                value={newAudioOverlay.volume} 
                onChange={(e) => setNewAudioOverlay({...newAudioOverlay, volume: parseFloat(e.target.value)})}
                style={{ width: '100%' }}
              />
              <span style={{ fontSize: '12px', color: '#666' }}>{Math.round(newAudioOverlay.volume * 100)}%</span>
            </div>
          </div>

          <button 
            onClick={addAudioOverlay}
            style={{
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Add Audio Overlay
          </button>
        </div>

        {/* Existing Audio Overlays List */}
        {audioOverlays.length > 0 ? (
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <h5 style={{ margin: '0 0 12px 0', color: '#555' }}>Added Audio Overlays</h5>
            {audioOverlays.map((overlay) => (
              <div 
                key={overlay.id} 
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '12px',
                  margin: '8px 0',
                  backgroundColor: 'white'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 'bold', color: '#333' }}>{overlay.file.name}</span>
                  <button 
                    onClick={() => removeAudioOverlay(overlay.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Remove
                  </button>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px', color: '#666' }}>
                  <div>Time: {overlay.startTime}s - {overlay.endTime}s</div>
                  <div>Type: {overlay.audioType === 'background' ? 'Background' : 'Only Audio'}</div>
                  <div>Volume: {Math.round(overlay.volume * 100)}%</div>
                  <div>Duration: {(overlay.endTime - overlay.startTime).toFixed(1)}s</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: '#666', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
            No audio overlays added yet
          </div>
        )}
      </div>

      <div style={{ marginTop: '10px' }}>
        <label>Filter:</label>
        <select value={filters} onChange={(e) => setFilters(e.target.value)}>
          <option value="none">None</option>
          <option value="grayscale(1)">Grayscale</option>
          <option value="sepia(1)">Sepia</option>
          <option value="contrast(2)">High Contrast</option>
          <option value="boxblur=2:1">Blur</option>
        </select>
      </div>

      <div style={{ marginTop: '10px' }}>
        <label>
          <input type="checkbox" checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} /> Show Grid Overlay
        </label>
      </div>

      <div style={{ marginTop: '20px' }}>
        <h4>Timeline Markers</h4>
        <input type="text" value={newMarkerTime} onChange={(e) => setNewMarkerTime(e.target.value)} placeholder="Enter time (s)" />
        <button onClick={addTimelineMarker}>Add Marker</button>
        <ul>
          {timelineMarkers.map((m, i) => <li key={i}>Marker at {m}s</li>)}
        </ul>
      </div>

      <div style={{ marginTop: '20px' }}>
        <h4>Tracks</h4>
        <ul>
          {tracks.map((track, i) => (
            <li key={i}>{track.name}</li>
          ))}
        </ul>
        <button onClick={() => setTracks([...tracks, { id: tracks.length + 1, name: `Track ${tracks.length + 1}` }])}>Add Track</button>
      </div>

      <div style={{ marginTop: '20px' }}>
        <button 
          onClick={handleSubmit}
          disabled={isSubmitting}
          style={{
            padding: '10px 20px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            opacity: isSubmitting ? 0.7 : 1
          }}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Edit'}
        </button>
      </div>
      <Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity={snackbar.severity}
					variant="filled"
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
    </div>
  );
};

export default SimulatedEditor;
